import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Car,
  Search,
  Bell,
  Plus,
  LayoutGrid,
  List,
  TrendingUp,
  Wallet,
  ChevronRight,
  Pencil,
  Trash2,
  XCircle,
  Loader2,
  BarChart3
} from 'lucide-react';
import DashboardSwitcher from '../components/layout/DashboardSwitcher';
import OwnerTopNav from '../components/layout/OwnerTopNav';
import Sidebar from '../components/layout/Sidebar';
import { ownerService } from '../services/owner.service';
import { showAppAlert } from '../utils/appAlert';
import { buildPublicFileUrl } from '../utils/fileUrl';

const MyFleet = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('list');
  const [activeFilter, setActiveFilter] = useState('All Vehicles');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [fleetData, setFleetData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [analyticsCar, setAnalyticsCar] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const fetchFleet = async () => {
    try {
      setLoading(true);
      const data = await ownerService.getOwnerFleet();
      setFleetData(data);
    } catch (error) {
      console.error("Failed to fetch fleet data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFleet();
  }, []);

  const filters = ['All Vehicles', 'Available', 'Pending Approval', 'Rejected', 'Rented', 'Maintenance', 'Suspended'];
  const buildImageUrl = (imageUrl) => {
    if (!imageUrl) return '';
    return buildPublicFileUrl(imageUrl);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'AVAILABLE': return 'bg-[#10B981]';
      case 'BOOKED':
      case 'IN_USE': return 'bg-[#FF6B00]';
      case 'MAINTENANCE': return 'bg-[#F59E0B]';
      case 'REJECTED': return 'bg-[#EF4444]';
      case 'PENDING_APPROVAL': return 'bg-[#F59E0B]';
      case 'SUSPENDED': return 'bg-[#6B7280]';
      default: return 'bg-gray-400';
    }
  };

  const formatStatusLabel = (status) => {
    if (!status) return 'UNKNOWN';
    return status.replace(/_/g, ' ');
  };

  const formatMoney = (value) => `RWF ${Math.round(Number(value || 0)).toLocaleString()}`;

  const filteredFleet = useMemo(() => {
    return fleetData.filter((car) => {
      const query = searchQuery.trim().toLowerCase();
      const matchesSearch = !query || `${car.name || ''} ${car.year || ''} ${car.status || ''}`.toLowerCase().includes(query);

      let matchesFilter = true;
      switch (activeFilter) {
        case 'Available':
          matchesFilter = car.status === 'AVAILABLE';
          break;
        case 'Pending Approval':
          matchesFilter = car.status === 'PENDING_APPROVAL';
          break;
        case 'Rejected':
          matchesFilter = car.status === 'REJECTED';
          break;
        case 'Rented':
          matchesFilter = car.status === 'BOOKED' || car.status === 'IN_USE';
          break;
        case 'Maintenance':
          matchesFilter = car.status === 'MAINTENANCE';
          break;
        case 'Suspended':
          matchesFilter = car.status === 'SUSPENDED';
          break;
        default:
          matchesFilter = true;
      }

      return matchesSearch && matchesFilter;
    });
  }, [fleetData, searchQuery, activeFilter]);

  const openAnalytics = async (car) => {
    try {
      setAnalyticsCar(car);
      setAnalyticsLoading(true);
      const data = await ownerService.getOwnerFleetAnalytics(car.id);
      setAnalyticsData(data);
      setActiveDropdown(null);
    } catch (error) {
      showAppAlert({ type: 'error', title: 'Analytics Failed', message: error.message || 'Failed to load analytics' });
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleRemoveFromPlatform = async (car) => {
    try {
      setActionLoadingId(car.id);
      await ownerService.removeFleetFromPlatform(car.id);
      await fetchFleet();
      setActiveDropdown(null);
    } catch (error) {
      showAppAlert({ type: 'error', title: 'Update Failed', message: error.message || 'Failed to remove vehicle from platform' });
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDelete = async (car) => {
    if (!window.confirm(`Delete ${car.name} from the platform? This cannot be undone.`)) {
      return;
    }
    try {
      setActionLoadingId(car.id);
      await ownerService.deleteFleet(car.id);
      await fetchFleet();
      setActiveDropdown(null);
    } catch (error) {
      showAppAlert({ type: 'error', title: 'Delete Failed', message: error.message || 'Failed to delete vehicle' });
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <div className="flex h-screen bg-[#F8F9FA] font-sans text-[#1F1F1F] overflow-hidden">

      {/* Sidebar */}
      <Sidebar activePage="Fleet" />

      {/* Main Content */}
      <main className="flex-grow overflow-y-auto">
        {/* Header/Topbar */}
        <header className="bg-white border-b border-gray-100 flex items-center justify-between px-10 py-5 sticky top-0 z-20">
          <div className="relative w-96">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search your fleet..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border-transparent rounded-xl text-sm font-medium focus:bg-white focus:ring-2 focus:ring-brand-orange-500 transition-all outline-none"
            />
          </div>

          <div className="flex items-center gap-6">
            <button
              onClick={() => navigate('/add-vehicle')}
              className="bg-[#FF6B00] text-white px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-brand-orange-700 transition-all shadow-lg shadow-brand-orange-100"
            >
              <Plus className="w-4 h-4" />
              Add Vehicle
            </button>
            <button className="p-2.5 text-gray-400 bg-gray-50 rounded-xl hover:text-brand-black transition-all relative">
              <Bell className="w-5 h-5" />
              <span className="absolute top-2.5 right-2.5 w-1.5 h-1.5 bg-red-500 rounded-full border-2 border-white"></span>
            </button>
          </div>
        </header>

        <div className="p-10">
          <div className="mb-4"><DashboardSwitcher active="/owner-dashboard"/></div>
          <OwnerTopNav activePage="Fleet"/>
          {/* Section Header */}
          <div className="flex items-end justify-between mb-10">
            <div>
              <h1 className="text-4xl font-black tracking-tight mb-2">MY FLEET</h1>
              <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">Manage and monitor your premium performance vehicles.</p>
            </div>

            <div className="bg-gray-100 p-1.5 rounded-2xl flex gap-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2.5 rounded-xl transition-all flex items-center gap-2 ${viewMode === 'grid' ? 'bg-white shadow-sm text-brand-black' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <LayoutGrid className="w-4 h-4" />
                <span className="text-[10px] font-black uppercase tracking-widest">Grid</span>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2.5 rounded-xl transition-all flex items-center gap-2 ${viewMode === 'list' ? 'bg-white shadow-sm text-brand-black' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <List className="w-4 h-4" />
                <span className="text-[10px] font-black uppercase tracking-widest">List</span>
              </button>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex items-center gap-8 mb-10 border-b border-gray-100">
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setActiveFilter(f)}
                className={`pb-4 px-2 text-[11px] font-black uppercase tracking-widest transition-all relative ${activeFilter === f ? 'text-brand-black' : 'text-gray-400 hover:text-gray-600'}`}
              >
                {f}
                {activeFilter === f && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-brand-orange-700 rounded-full"></div>}
              </button>
            ))}
          </div>

          {/* Grid View */}
          {loading ? (
             <div className="flex justify-center items-center h-48">
               <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
             </div>
          ) : viewMode === 'grid' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              {filteredFleet.map((car) => (
                <div key={car.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden group hover:shadow-2xl hover:shadow-gray-200/50 transition-all duration-500">
                  <div className="relative aspect-[4/3] overflow-hidden bg-gray-100 flex justify-center items-center">
                    {car.heroImage || car.images?.hero ? (
                      <img src={buildImageUrl(car.heroImage || car.images?.hero)} alt={car.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    ) : (
                      <Car className="w-16 h-16 text-gray-300" />
                    )}
                    <div className={`absolute top-6 left-6 px-4 py-2 rounded-xl text-[8px] font-black text-white uppercase tracking-widest ${getStatusColor(car.status)}`}>
                      {formatStatusLabel(car.status)}
                    </div>
                  </div>

                  <div className="p-8">
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <h3 className="text-xl font-black mb-1">{car.name}</h3>
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">{car.year}</p>
                      </div>
                      <span className="text-sm font-bold text-gray-400 uppercase tracking-widest">2023</span>
                    </div>

                    <div className="flex items-center gap-6 mb-8 text-[11px] font-bold">
                      <div className="flex items-center gap-2 text-gray-400">
                        <Wallet className="w-4 h-4" />
                        <span>{formatMoney(car.rate)}/day</span>
                      </div>
                      <div className="flex items-center gap-2 text-green-600 bg-green-50 px-3 py-1.5 rounded-lg">
                        <TrendingUp className="w-4 h-4" />
                        <span>{formatMoney(car.earnings)} total revenue</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 relative">
                      {car.status === 'DRAFT' || car.status === 'REJECTED' ? (
                        <button
                           onClick={() => navigate(`/add-vehicle?id=${car.id}`)}
                           className={`flex-grow py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest border border-transparent transition-all ${car.status === 'REJECTED' ? 'bg-red-50 text-red-600 hover:border-red-200 hover:bg-red-100' : 'bg-brand-orange-50 text-brand-black hover:border-brand-orange-200 hover:bg-brand-orange-100'}`}
                        >
                           {car.status === 'REJECTED' ? 'Update & Resubmit' : 'Resume Draft'}
                        </button>
                      ) : (
                        <button
                          onClick={() => navigate(`/owner/car/${car.id}`)}
                          className="flex-grow bg-gray-50 text-brand-black py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest border border-transparent hover:border-gray-200 hover:bg-gray-100 transition-all"
                        >
                          Details
                        </button>
                      )}
                      <button
                        onClick={() => openAnalytics(car)}
                        className="flex-grow bg-gray-50 text-brand-black py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest border border-transparent hover:border-gray-200 hover:bg-gray-100 transition-all"
                      >
                        Analytics
                      </button>
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveDropdown(activeDropdown === car.id ? null : car.id);
                          }}
                          className={`px-4 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all border ${
                            activeDropdown === car.id
                              ? 'bg-brand-black text-white border-brand-black'
                              : 'bg-gray-50 text-gray-600 border-gray-100 hover:text-brand-black hover:border-gray-200'
                          }`}
                        >
                          More Actions
                        </button>

                        {activeDropdown === car.id && (
                          <div className="absolute bottom-full right-0 mb-4 w-56 bg-white rounded-2xl shadow-2xl border border-gray-100 py-3 z-50 animate-in fade-in zoom-in duration-200">
                            {(car.status === 'DRAFT' || car.status === 'REJECTED') && (
                              <button
                                onClick={() => navigate(`/add-vehicle?id=${car.id}`)}
                                className="w-full flex items-center gap-3 px-6 py-3.5 text-xs font-black uppercase tracking-widest text-gray-600 hover:text-brand-black hover:bg-gray-50 transition-all"
                              >
                                <Pencil className="w-4 h-4" />
                                Edit
                              </button>
                            )}
                            <button
                              onClick={() => openAnalytics(car)}
                              className="w-full flex items-center gap-3 px-6 py-3.5 text-xs font-black uppercase tracking-widest text-gray-600 hover:text-brand-black hover:bg-gray-50 transition-all"
                            >
                              <BarChart3 className="w-4 h-4" />
                              Analytics
                            </button>
                            {car.canRemoveFromPlatform && (
                              <button
                                onClick={() => handleRemoveFromPlatform(car)}
                                disabled={actionLoadingId === car.id}
                                className="w-full flex items-center gap-3 px-6 py-3.5 text-xs font-black uppercase tracking-widest text-gray-600 hover:text-brand-black hover:bg-gray-50 transition-all disabled:opacity-50"
                              >
                                <XCircle className="w-4 h-4" />
                                Remove From Platform
                              </button>
                            )}
                            {car.canDelete && (
                              <>
                                <div className="mx-6 my-2 border-t border-gray-50"></div>
                                <button
                                  onClick={() => handleDelete(car)}
                                  disabled={actionLoadingId === car.id}
                                  className="w-full flex items-center gap-3 px-6 py-3.5 text-xs font-black uppercase tracking-widest text-red-500 hover:bg-red-50 transition-all disabled:opacity-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* List View */}
          {!loading && viewMode === 'list' && (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-visible animate-in fade-in slide-in-from-right-4 duration-500">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-50 bg-gray-50/30">
                    <th className="text-left py-6 px-10 text-[10px] font-black uppercase tracking-widest text-gray-400">Vehicle</th>
                    <th className="text-center py-6 px-10 text-[10px] font-black uppercase tracking-widest text-gray-400">Status</th>
                    <th className="text-center py-6 px-10 text-[10px] font-black uppercase tracking-widest text-gray-400">Earnings</th>
                    <th className="text-right py-6 px-10 text-[10px] font-black uppercase tracking-widest text-gray-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredFleet.map((car) => (
                    <tr key={car.id} className="hover:bg-gray-50/50 transition-colors group">
                      <td className="py-6 px-10">
                        <div className="flex items-center gap-6">
                          {car.heroImage || car.images?.hero ? (
                            <img src={buildImageUrl(car.heroImage || car.images?.hero)} alt={car.name} className="w-16 h-16 rounded-2xl object-cover shadow-sm" />
                          ) : (
                            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex justify-center items-center shadow-sm">
                              <Car className="w-6 h-6 text-gray-300" />
                            </div>
                          )}
                          <div>
                            <h4 className="font-black text-sm">{car.name}</h4>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{car.year} • {formatMoney(car.rate)}/day</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-6 px-10">
                        <div className="flex justify-center">
                          <span className={`px-4 py-2 rounded-xl text-[8px] font-black text-white uppercase tracking-widest ${getStatusColor(car.status)}`}>
                            {formatStatusLabel(car.status)}
                          </span>
                        </div>
                      </td>
                      <td className="py-6 px-10">
                        <div className="text-center">
                          <span className="block text-sm font-black text-green-600">{formatMoney(car.earnings)}</span>
                          <span className="text-[8px] font-black text-gray-300 uppercase tracking-widest">Revenue</span>
                        </div>
                      </td>
                      <td className="py-6 px-10">
                        <div className="flex items-center justify-end gap-3 relative">
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveDropdown(activeDropdown === car.id ? null : car.id);
                              }}
                              className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border ${
                                activeDropdown === car.id
                                  ? 'bg-brand-black text-white border-brand-black'
                                  : 'bg-gray-50 text-gray-600 border-gray-100 hover:text-brand-black hover:border-gray-200'
                              }`}
                            >
                              More Actions
                            </button>

                            {activeDropdown === car.id && (
                              <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-2xl shadow-2xl border border-gray-100 py-3 z-50 animate-in fade-in zoom-in duration-200">
                                {car.status === 'DRAFT' || car.status === 'REJECTED' ? (
                                  <button
                                    onClick={() => navigate(`/add-vehicle?id=${car.id}`)}
                                    className={`w-full flex items-center gap-3 px-6 py-3.5 text-xs font-black uppercase tracking-widest transition-all ${car.status === 'REJECTED' ? 'text-red-600 hover:bg-red-50' : 'text-brand-black hover:bg-brand-orange-50 hover:text-brand-black'}`}
                                  >
                                    <Pencil className="w-4 h-4 text-gray-400" />
                                    {car.status === 'REJECTED' ? 'Update & Resubmit' : 'Resume Draft'}
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => navigate(`/owner/car/${car.id}`)}
                                    className="w-full flex items-center gap-3 px-6 py-3.5 text-xs font-black uppercase tracking-widest text-brand-black hover:bg-gray-50 transition-all"
                                  >
                                    <ChevronRight className="w-4 h-4 text-gray-400" />
                                    Details
                                  </button>
                                )}
                                <button
                                  onClick={() => openAnalytics(car)}
                                  className="w-full flex items-center gap-3 px-6 py-3.5 text-xs font-black uppercase tracking-widest text-brand-black hover:bg-gray-50 transition-all"
                                >
                                  <TrendingUp className="w-4 h-4 text-gray-400" />
                                  Analytics
                                </button>
                                {car.canRemoveFromPlatform && (
                                  <button
                                    onClick={() => handleRemoveFromPlatform(car)}
                                    disabled={actionLoadingId === car.id}
                                    className="w-full flex items-center gap-3 px-6 py-3.5 text-xs font-black uppercase tracking-widest text-gray-600 hover:text-brand-black hover:bg-gray-50 transition-all disabled:opacity-50"
                                  >
                                    <XCircle className="w-4 h-4" />
                                    Remove From Platform
                                  </button>
                                )}
                                {car.canDelete && (
                                  <>
                                    <div className="mx-6 my-2 border-t border-gray-50"></div>
                                    <button
                                      onClick={() => handleDelete(car)}
                                      disabled={actionLoadingId === car.id}
                                      className="w-full flex items-center gap-3 px-6 py-3.5 text-xs font-black uppercase tracking-widest text-red-500 hover:bg-red-50 transition-all disabled:opacity-50"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                      Delete
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {analyticsCar && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6">
            <div className="w-full max-w-2xl bg-white rounded-3xl border border-gray-100 shadow-2xl p-8">
              <div className="flex items-start justify-between gap-4 mb-8">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Fleet Analytics</p>
                  <h3 className="text-2xl font-black text-brand-black">{analyticsCar.name}</h3>
                </div>
                <button onClick={() => { setAnalyticsCar(null); setAnalyticsData(null); }} className="text-gray-400 hover:text-brand-black font-black">Close</button>
              </div>

              {analyticsLoading ? (
                <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
              ) : analyticsData && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="rounded-2xl bg-gray-50 p-6"><p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Total Bookings</p><p className="text-3xl font-black">{analyticsData.totalBookings || 0}</p></div>
                  <div className="rounded-2xl bg-gray-50 p-6"><p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Completed</p><p className="text-3xl font-black">{analyticsData.completedBookings || 0}</p></div>
                  <div className="rounded-2xl bg-gray-50 p-6"><p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Active</p><p className="text-3xl font-black">{analyticsData.activeBookings || 0}</p></div>
                  <div className="rounded-2xl bg-gray-50 p-6"><p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Pending</p><p className="text-3xl font-black">{analyticsData.pendingBookings || 0}</p></div>
                  <div className="rounded-2xl bg-gray-50 p-6"><p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Total Revenue</p><p className="text-3xl font-black">{formatMoney(analyticsData.totalRevenue)}</p></div>
                  <div className="rounded-2xl bg-gray-50 p-6"><p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Average Booking</p><p className="text-3xl font-black">{formatMoney(analyticsData.averageBookingValue)}</p></div>
                  <div className="rounded-2xl bg-gray-50 p-6 md:col-span-2"><p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Last Booking</p><p className="text-xl font-black">{analyticsData.lastBookingDate || 'No bookings yet'}</p></div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default MyFleet;
